'use client';
import Head from 'next/head';
import { useEffect, useMemo, useState } from 'react';
import { Divide } from 'lucide-react';
import MethodPageLayout from '@/components/layouts/MethodPageLayout';
import BusStopMethod from '@/components/methods/BusStopMethod';
import { parseIntStrict } from '@/lib/utils';

export default function BusStopMethodPage() {
    const [dividendInput, setDividendInput] = useState('');
    const [divisorInput, setDivisorInput] = useState('');
    const [showText, setShowText] = useState(true);
    const [showImage, setShowImage] = useState(true);
    const [submitted, setSubmitted] = useState(false);

    function setRandomEquation() {
        function randInt(min: number, max: number): number {
            return Math.floor(Math.random() * (max - min + 1)) + min;
        }
        const divisor = randInt(2, 99);
        const upper = Math.max(divisor + 1, randInt(100, 99999));
        const dividend = randInt(divisor, upper);
        setDividendInput(String(dividend));
        setDivisorInput(String(divisor));
    }

    useEffect(() => {
        if (dividendInput !== '' || divisorInput !== '') return;
        setRandomEquation();
    }, []);

    const parsedDividend = useMemo(() => parseIntStrict(dividendInput), [dividendInput]);
    const parsedDivisor = useMemo(() => parseIntStrict(divisorInput), [divisorInput]);

    function onSolve() {
        setSubmitted(true);
    }

    function onClear() {
        setDividendInput('');
        setDivisorInput('');
        setSubmitted(false);
    }

    const onReset = () => {
        setRandomEquation();
        setSubmitted(false);
    };

    const invalid = Number.isNaN(parsedDividend) || Number.isNaN(parsedDivisor);

    const labelClass = 'text-[11px] font-medium uppercase text-muted';
    const checkboxLabelClass = 'flex cursor-pointer items-center gap-2 text-[13px]';

    const controls = (
        <>
            <div className="flex flex-col gap-2">
                <label className={labelClass}>Dividend (inside)</label>
                <input
                    type="number"
                    placeholder="e.g., 8273"
                    value={dividendInput}
                    onChange={(e) => setDividendInput(e.target.value)}
                />
            </div>
            <div className="flex flex-col gap-2">
                <label className={labelClass}>Divisor (outside)</label>
                <input
                    type="number"
                    placeholder="e.g., 32"
                    value={divisorInput}
                    onChange={(e) => setDivisorInput(e.target.value)}
                />
            </div>

            <div className="mt-1 flex flex-col gap-2.5">
                <label className={checkboxLabelClass}>
                    <input
                        type="checkbox"
                        checked={showText}
                        onChange={(e) => setShowText(e.target.checked)}
                        className="w-auto"
                    />
                    Text explanation
                </label>
                <label className={checkboxLabelClass}>
                    <input
                        type="checkbox"
                        checked={showImage}
                        onChange={(e) => setShowImage(e.target.checked)}
                        className="w-auto"
                    />
                    Image (canvas)
                </label>
            </div>

            <div className="mt-2 flex gap-2">
                <button className="primary flex-1" onClick={onSolve}>
                    Solve
                </button>
                <button className="flex-1" onClick={onClear}>
                    Clear
                </button>
            </div>
        </>
    );

    return (
        <>
            <Head>
                <title>Bus Stop Method Solver</title>
            </Head>
            <MethodPageLayout
                icon={<Divide size={20} />}
                iconColor="purple"
                title="Bus stop method"
                description="Solves division questions step-by-step using the Bus Stop method."
                category="method"
                controls={controls}
                onReset={onReset}
            >
                <div className="w-full max-w-[800px]">
                    {submitted && !invalid && (
                        <BusStopMethod
                            dividend={parsedDividend}
                            divisor={parsedDivisor}
                            showText={showText}
                            showImage={showImage}
                        />
                    )}

                    {submitted && invalid && (
                        <div className="rounded-lg border-[0.5px] border-line bg-white p-5 text-center text-coral">
                            Please provide valid numbers for dividend and divisor.
                        </div>
                    )}

                    {!submitted && (
                        <div className="p-10 text-center text-hint">
                            Enter numbers and click Solve to see the step-by-step division.
                        </div>
                    )}
                </div>
            </MethodPageLayout>
        </>
    );
}
